// Biến toàn cục (module-level) để giữ promise của quá trình refresh
let refreshPromise = null;

// Hàm trợ giúp để quản lý token
export const tokenManager = { // Export tokenManager
  getAccessToken: () => localStorage.getItem('accessToken'),
  getRefreshToken: () => localStorage.getItem('refreshToken'),
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('accessToken', accessToken);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
  },
  clearTokens: () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    // Removed: window.location.href = '/'; // Điều hướng về trang đăng nhập
  }
};

// Hàm fetch được bọc lại với logic refresh token
// Added navigate parameter (kept for potential future use or if components still pass it, but not used for navigation here)
export async function authFetch(url, options = {}, navigate = null, showToast = null) {
  // Nếu không có accessToken, gọi fetch thường (API public)
  if (!tokenManager.getAccessToken()) {
    return fetch(url, options);
  }
  // Lần thử đầu tiên với token hiện tại
  let response = await fetchWithToken(url, options);

  // Nếu không phải lỗi 401, trả về ngay
  if (response.status !== 401) {
    return response;
  }

  // Xử lý khi gặp lỗi 401 (token hết hạn)
  // Nếu chưa có ai đang đi refresh, hãy là người đầu tiên
  if (!refreshPromise) {
    let resolveRefresh;
    let rejectRefresh;
    refreshPromise = new Promise((resolve, reject) => {
        resolveRefresh = resolve;
        rejectRefresh = reject;
    });

    (async () => {
      try {
        const refreshToken = tokenManager.getRefreshToken();
        if (!refreshToken) {
          // Không có refresh token: clear token, show toast, chuyển hướng login nếu cần
          tokenManager.clearTokens();
          if (showToast) showToast('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.', 'error');
          if (navigate) navigate('/');
          rejectRefresh(new Error('Không có refresh token để làm mới.'));
          return;
        }
        const API_BASE = process.env.REACT_APP_TEST_API_BASE_URL;
        const accessToken = tokenManager.getAccessToken();
        const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Access-Token': accessToken,
            'Refresh-Token': refreshToken,
          },
        });
        const newAccessToken = refreshRes.headers.get('Access-Token');
        const newRefreshToken = refreshRes.headers.get('Refresh-Token');
        if (!newAccessToken || !newRefreshToken) {
          throw new Error('Không nhận được accessToken hoặc refreshToken từ header.');
        }
        tokenManager.setTokens(newAccessToken, newRefreshToken);
        resolveRefresh();
      } catch (error) {
        tokenManager.clearTokens();
        if (showToast) showToast('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.', 'error');
        if (navigate) navigate('/');
        rejectRefresh(error);
      } finally {
        refreshPromise = null;
      }
    })();
  }

  // Wait for the refresh process to complete
  try {
      await refreshPromise;
  } catch (refreshError) {
      // Nếu refresh thất bại, trả về response giả với status 401 để không throw liên tục
      return { status: 401, ok: false, json: async () => ({ code: 401, message: 'Phiên đăng nhập đã hết hạn.' }) };
  }

  // Sau khi refresh xong, thử lại request ban đầu với token mới
  response = await fetchWithToken(url, options);

  if (response.status === 401) {
    tokenManager.clearTokens();
    if (showToast) showToast('Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại.', 'error');
    if (navigate) navigate('/');
    return { status: 401, ok: false, json: async () => ({ code: 401, message: 'Phiên đăng nhập đã hết hạn.' }) };
  }

  return response;
}

// Hàm con để thực hiện fetch với token, tránh lặp code
function fetchWithToken(url, options = {}) {
  const token = tokenManager.getAccessToken();
  const headers = { ...options.headers };

  // Chỉ set Content-Type nếu body không phải là FormData
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return fetch(url, { ...options, headers });
}
